import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress,
  Alert, TextField, InputAdornment, Button, Tabs, Tab, Pagination, Tooltip
} from '@mui/material';
import { Visibility, Edit, Delete, Search, Add, Send, Email, FileCopy } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function KlimaProtokollListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [protokolle, setProtokolle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 = Alle, 1 = Entwürfe, 2 = Gesendet
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20);

  useEffect(() => {
    fetchProtokolle();
  }, []);

  const fetchProtokolle = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/klima-protocols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const protokolleData = response.data || [];
      
      // Sort by date (newest first)
      const sorted = protokolleData.sort((a, b) => 
        new Date(b.created_at || b.datum) - new Date(a.created_at || a.datum)
      );
      setProtokolle(sorted);
      setError('');
    } catch (err) {
      console.error('Error fetching protokolle:', err);
      setError('Fehler beim Laden der Protokolle');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie dieses Protokoll wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/klima-protocols/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProtokolle(protokolle.filter(p => p._id !== id));
    } catch (err) {
      console.error('Error deleting protokoll:', err);
      alert('Fehler beim Löschen des Protokolls');
    }
  };

  const handleView = (protokoll) => {
    // Open PDF if exists
    if (protokoll.pdf_id) {
      window.open(`${API_URL}/klima-protocols/pdf/${protokoll.pdf_id}`, '_blank');
    } else {
      alert('PDF nicht verfügbar');
    }
  };

  const handleEdit = (protokoll) => {
    // Navigate to form with existing data for editing
    navigate('/protocol/form', { 
      state: { 
        editMode: true,
        formData: protokoll
      }
    });
  };

  const handleSend = async (protokoll) => {
    if (!window.confirm('Möchten Sie diesen Entwurf jetzt als Protokoll versenden?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Update status to "submitted" - this will trigger PDF generation and email sending
      await axios.put(
        `${API_URL}/klima-protocols/${protokoll._id}`,
        { ...protokoll, status: 'submitted' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Protokoll erfolgreich versendet!');
      fetchProtokolle(); // Refresh list
    } catch (err) {
      console.error('Error sending protokoll:', err);
      alert('Fehler beim Versenden des Protokolls');
    }
  };

  const handleResendEmail = async (protokoll) => {
    if (!window.confirm('PDF erneut per Email versenden?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const email = window.prompt('Email-Adresse:', protokoll.kunde_email || '');
      
      if (!email) return;

      // Call resend endpoint
      await axios.post(
        `${API_URL}/klima-protocols/${protokoll._id}/resend`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Email erfolgreich versendet!');
    } catch (err) {
      console.error('Error resending email:', err);
      alert('Fehler beim Versenden der Email');
    }
  };

  const handleUseAsTemplate = async (protokoll) => {
    if (!window.confirm('Dieses Protokoll als Vorlage verwenden?')) {
      return;
    }

    // Navigate to form with template data (ohne Bilder, Unterschriften, Auftragsnummer)
    const templateData = {
      ...protokoll,
      auftragsnummer: '',
      images: [],
      monteur_unterschrift: '',
      kunde_unterschrift: '',
      kunde_name_unterschrift: '',
      datum: new Date().toISOString().split('T')[0],
      status: 'draft'
    };

    navigate('/protocol/klima-form', { state: { templateData } });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Filter based on search query
  const filteredProtokolle = protokolle.filter(p => {
    const matchesSearch = !searchQuery || 
      p.kunde_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.ku_nr?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.auftragsnummer?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter by tab
    if (tabValue === 1) return p.status === 'draft' && matchesSearch;
    if (tabValue === 2) return p.status === 'submitted' && matchesSearch;
    return matchesSearch;
  });

  // Sort by date descending (newest first)
  const sortedProtokolle = [...filteredProtokolle].sort((a, b) => {
    return new Date(b.created_at || b.datum) - new Date(a.created_at || a.datum);
  });

  // Paginate
  const totalPages = Math.ceil(sortedProtokolle.length / rowsPerPage);
  const paginatedProtokolle = sortedProtokolle.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (loading) {
    return (
      <Box>
        <AppHeader />
        <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <AppHeader />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Wartungsprotokolle Klimaanlage
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/protocol/customer-search')}
            >
              Neues Protokoll
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Suche nach Kunde, Kundennummer oder Auftragsnummer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label={`Alle (${protokolle.length})`} />
            <Tab label={`Entwürfe (${protokolle.filter(p => p.status === 'draft').length})`} />
            <Tab label={`Gesendet (${protokolle.filter(p => p.status === 'submitted').length})`} />
          </Tabs>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Datum</TableCell>
                  <TableCell>Auftragsnummer</TableCell>
                  <TableCell>Kunde</TableCell>
                  <TableCell>Kd-Nr</TableCell>
                  <TableCell>Hersteller</TableCell>
                  <TableCell>Monteur</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProtokolle.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Keine Protokolle gefunden
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedProtokolle.map((protokoll) => (
                    <TableRow key={protokoll._id} hover>
                      <TableCell>{formatDate(protokoll.datum)}</TableCell>
                      <TableCell>{protokoll.auftragsnummer || '-'}</TableCell>
                      <TableCell>{protokoll.kunde_name}</TableCell>
                      <TableCell>{protokoll.ku_nr || '-'}</TableCell>
                      <TableCell>
                        {protokoll.hersteller === 'Sonstiger' 
                          ? protokoll.hersteller_sonstiger || 'Sonstiger'
                          : protokoll.hersteller || '-'}
                      </TableCell>
                      <TableCell>{protokoll.monteur || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={protokoll.status === 'draft' ? 'Entwurf' : 'Gesendet'}
                          color={protokoll.status === 'draft' ? 'warning' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {protokoll.status === 'draft' ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(protokoll)}
                              title="Bearbeiten"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleSend(protokoll)}
                              title="Senden"
                              color="primary"
                            >
                              <Send />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(protokoll._id)}
                              title="Löschen"
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <Tooltip title="PDF anzeigen">
                              <IconButton
                                size="small"
                                onClick={() => handleView(protokoll)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Email erneut senden">
                              <IconButton
                                size="small"
                                onClick={() => handleResendEmail(protokoll)}
                                color="info"
                              >
                                <Email />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Als Vorlage verwenden">
                              <IconButton
                                size="small"
                                onClick={() => handleUseAsTemplate(protokoll)}
                                color="success"
                              >
                                <FileCopy />
                              </IconButton>
                            </Tooltip>
                            {user?.role === 'admin' && (
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(protokoll._id)}
                                title="Löschen"
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination 
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
